import React, { useState } from 'react';

const SearchPanel = ({ onSearch, results, apiBaseUrl }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [dataType, setDataType] = useState('analyzed');
  const [searchLimit, setSearchLimit] = useState(10);
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      await onSearch(searchQuery, dataType, searchLimit);
    } finally {
      setIsSearching(false);
    }
  };

  const handleQuickSearch = (query) => {
    setSearchQuery(query);
    onSearch(query, dataType, searchLimit);
  };

  const quickSearches = [
    'critical minerals',
    'rare earth elements',
    'lithium mining',
    'cobalt supply',
    'nickel production',
    'battery materials'
  ];

  return (
    <div className="space-y-6">
      {/* Search Form */}
      <form onSubmit={handleSearch} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Search Query
          </label>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Enter search terms (e.g., 'critical minerals', 'lithium')"
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Data Type
            </label>
            <select
              value={dataType}
              onChange={(e) => setDataType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="analyzed">Analyzed Data</option>
              <option value="raw">Raw Data</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Results Limit
            </label>
            <select
              value={searchLimit}
              onChange={(e) => setSearchLimit(parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value={5}>5 results</option>
              <option value={10}>10 results</option>
              <option value={20}>20 results</option>
              <option value={50}>50 results</option>
            </select>
          </div>
        </div>

        <button
          type="submit"
          disabled={isSearching || !searchQuery.trim()}
          className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
            isSearching || !searchQuery.trim()
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
          }`}
        >
          {isSearching ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Searching...</span>
            </div>
          ) : (
            <div className="flex items-center justify-center space-x-2">
              <span>🔍</span>
              <span>Search Data</span>
            </div>
          )}
        </button>
      </form>

      {/* Quick Search Buttons */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Quick Searches
        </h4>
        <div className="flex flex-wrap gap-2">
          {quickSearches.map((query) => (
            <button
              key={query}
              onClick={() => handleQuickSearch(query)}
              className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {query}
            </button>
          ))}
        </div>
      </div>

      {/* Search Results */}
      {results.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Search Results ({results.length})
          </h4>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {results.map((result, index) => (
              <div
                key={index}
                className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900 dark:text-white text-sm mb-1">
                      {result.metadata?.headline || 'No title'}
                    </h5>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                      Source: {result.metadata?.source || 'Unknown'} | 
                      Date: {result.metadata?.published_date || 'Unknown'}
                    </p>
                    {result.metadata?.content && (
                      <p className="text-xs text-gray-700 dark:text-gray-300 line-clamp-2">
                        {result.metadata.content.substring(0, 150)}...
                      </p>
                    )}
                  </div>
                  <div className="ml-3 text-xs text-gray-500 dark:text-gray-400">
                    Score: {result.score?.toFixed(3) || 'N/A'}
                  </div>
                </div>
                
                {/* Analysis Results (for analyzed data) */}
                {dataType === 'analyzed' && result.metadata?.analysis && (
                  <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex flex-wrap gap-1">
                      {result.metadata.analysis.pestle_classification && (
                        <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 rounded">
                          {result.metadata.analysis.pestle_classification}
                        </span>
                      )}
                      {result.metadata.analysis.signal_type && (
                        <span className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200 rounded">
                          {result.metadata.analysis.signal_type}
                        </span>
                      )}
                      {result.metadata.analysis.pestle_risk_level && (
                        <span className="px-2 py-1 text-xs bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 rounded">
                          {result.metadata.analysis.pestle_risk_level}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Results Message */}
      {results.length === 0 && searchQuery && !isSearching && (
        <div className="text-center py-8">
          <div className="text-4xl mb-2">🔍</div>
          <p className="text-gray-600 dark:text-gray-400">
            No results found for "{searchQuery}"
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
            Try different keywords or check the data type selection
          </p>
        </div>
      )}

      {/* Search Tips */}
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
        <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
          Search Tips
        </h4>
        <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
          <li>• Use specific mineral names: "lithium", "cobalt", "nickel"</li>
          <li>• Search by topics: "mining", "supply chain", "battery"</li>
          <li>• Try company names: "Tesla", "MP Materials", "Lynas"</li>
          <li>• Use geographic terms: "China", "Australia", "DRC"</li>
        </ul>
      </div>
    </div>
  );
};

export default SearchPanel; 